import {
  LossRunsModalData,
  LossRunsRequestComponent,
} from './../../../../../../shared/ui-common/src/lib/components/modals/loss-runs-request/loss-runs-request.component';

import { Component, computed, effect, EffectRef, inject, OnInit, output, signal } from '@angular/core';
import { ActivatedRoute, Router} from '@angular/router';
import { LossRunsRequestDetailComponent } from './../../../../../../shared/ui-common/src/lib/components/modals/loss-runs-request-detail/loss-runs-request-detail.component';
import {
  LossRunsRequestGridViewModel,
  SubmissionSummaryViewModel,
  ViewSubmissionStore,
} from '@program-suite/data-common';
import { ButtonComponent, ModalService, ModalType } from '@program-suite/ui';
import { GridEmitter, GridOptions, UiGridComponent } from '@program-suite/ui-grid';

@Component({
  selector: 'lib-loss-runs',
  imports: [UiGridComponent, ButtonComponent],
  templateUrl: './loss-runs.component.html',
  styleUrl: './loss-runs.component.scss',
})
export class LossRunsComponent implements OnInit {
  router = inject(Router);
  modalService = inject(ModalService);
  viewSubmissionStore = inject(ViewSubmissionStore);
  changeTabEvent = output<string>();
  pageSizeMax = 2147483647;
  id: string | null = null;
  data = computed<LossRunsRequestGridViewModel[]>(
    () => (this.viewSubmissionStore.lossRuns?.()?.rows as LossRunsRequestGridViewModel[]) ?? [],
  );
  filterValues = signal<any>({ pageSize: this.pageSizeMax, sortOrder: { active: 'createdDate', direction: 'desc' } });
  gridOptions = signal<GridOptions>({
    filterData: [],
    filterValues: this.filterValues,
    openFilters: true,
    columns: [
      { header: 'Created', controlName: 'createDate', displayPipe: 'date' },
      { header: 'Type', controlName: 'type' },
      { header: 'Policy Term', controlName: 'policyTerm' },
      { header: 'Comments', controlName: 'comments' },
      { header: 'Status', controlName: 'status' },
      { header: 'Last Updated By', controlName: 'lastModifiedBy' },
      {
        header: 'Manage',
        controlName: 'action',
        actionColumnitems: [
          {
            type: 'dropdown',
            buttonAction: 'action',
            conditionalDisplay: { calculateDisplay: () => 'ellipsis', column: 'status' },
            dropdownItems: computed(() => {
              const items = [
                { name: 'View Request', buttonAction: 'viewRequest' },
                {
                  name: 'Download',
                  buttonAction: 'download',
                  conditionalDisabled: { calculateDisabled: (value: string) => value !== 'Issued', column: 'status' },
                },
              ];
              return items;
            }),
          },
        ],
      },
    ],
    data: this.data,
    frontEndPagination: true,
    fetchData: false,
    displaySearch: false,
    hidepagination: true,
  });
  summary = computed<SubmissionSummaryViewModel>(() => {
    return this.viewSubmissionStore.summary?.() ?? {};
  });
  private guidEffectRef?: EffectRef;

  constructor(private route: ActivatedRoute) {
    //effect to open modal if guid is present in query params
    this.guidEffectRef = effect(() => {
      const summary = this.summary();
      const lossRuns = this.viewSubmissionStore.lossRuns?.();
      if (summary && lossRuns) {
        const guid = this.route.snapshot.queryParamMap.get('guid');
        if (guid) {
          const rows = this.viewSubmissionStore.lossRuns?.()?.rows;
          if (rows && rows.length > 0) {
            const match = rows.find((r) => r.guid?.toLowerCase() === guid.toLowerCase());
            if (match) {
              this.viewRequest(match);
              this.router.navigate([], { relativeTo: this.route, queryParams: { guid: null }, queryParamsHandling: 'merge' });
              this.guidEffectRef?.destroy();
            }
          }
        }
      }
    });
  }

  async ngOnInit() {
    this.id = this.route.snapshot.paramMap.get('id');
  }

  fetchAll(rawParams?: any) {
    const params = {
      policyGuid: this.id,
    };

    return this.viewSubmissionStore.getLossRuns(params);
  }

  actionEvent(event: GridEmitter) {
    const data = event.data as any;
    if (event.action === 'fetchAll') {
      this.fetchAll(event.data);
    } else if (event.action === 'formChanges') {
      // this.fetchAll(event.data);
    } else if (event.action === 'clearFilters') {
      // this.fetchAll(event.data);
    } else if (event.action === 'onDestroy') {
      // this.submissionGridStore.setFilterValues(event.data ?? {});
    } else if (event.action === 'action') {
    } else if (event.action === 'viewRequest') {
      this.viewRequest(event.data);
    } else if (event.action === 'download') {
      this.download(data.guid);
    }
  }

  openRequestLossRunsModal() {
    this.modalService.open(
      LossRunsRequestComponent,
      {
        modalType: ModalType.Medium,
        data: {
          policyGuid: this.id,
          policyNumber: this.summary()?.policyNumber,
          businessName: this.summary()?.businessInfo?.businessName,
          policyTerm: this.formatPolicyTerm(
            this.summary()?.policyEffectiveDate!,
            this.summary()?.policyExpirationDate!,
          ),
        } as LossRunsModalData,
      },
      (result: any) => {
        this.fetchAll();
      },
    );
  }
  viewRequest(data: any) {
    this.modalService.open(LossRunsRequestDetailComponent, {
      modalType: ModalType.Medium,
      data: {
        policyNumber: this.summary()?.policyNumber,
        businessName: this.summary()?.businessInfo?.businessName,
        policyTerm: data.policyTerm,
        notes: data.notes,
        status: data.status,
        guid: data.guid,
      },
    });
  }

  download(lossRunsRequestGuid: string) {
    this.viewSubmissionStore.downloadLossRun(lossRunsRequestGuid);
  }

  formatDate(date: Date): string {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  formatPolicyTerm(start: string | Date, end: string | Date): string {
    // Parse date strings directly without timezone conversion
    const formatDateString = (date: string | Date): string => {
      if (typeof date === 'string') {
        const parts = date.split('T')[0].split('-');
        if (parts.length === 3) {
          const [yyyy, mm, dd] = parts;
          return `${mm}/${dd}/${yyyy}`;
        }
      }
      return this.formatDate(new Date(date));
    };

    return `${formatDateString(start)} - ${formatDateString(end)}`;
  }
}
